import { Either, Schema } from "effect";
import type { ClientConfig, Credential, Result } from "./api.ts";

/** Visible transport state, not a promise that a disconnected screen is current. */
export type ConnectionState =
  | "connecting"
  | "live"
  | "reconnecting"
  | "offline";
const envelope = Schema.Struct({
  type: Schema.String,
  id: Schema.optional(Schema.String),
  event: Schema.optional(Schema.Unknown),
  connectionTimeoutMs: Schema.optional(
    Schema.Number.pipe(Schema.greaterThan(0)),
  ),
});
// AWS documents a batch of serialized JSON events. Retain compatibility with
// single-event frames too; validate each batch member without discarding peers.
const eventBatch = Schema.transform(
  Schema.Union(Schema.String, Schema.Array(Schema.Unknown)),
  Schema.Array(Schema.Unknown),
  {
    decode: (value) => (typeof value === "string" ? [value] : value),
    encode: (value) => value,
  },
);

/** AWS AppSync Events subscription with acknowledged setup, keepalive, and backoff. */
export class OrderEvents {
  #socket: WebSocket | undefined;
  #retry: ReturnType<typeof setTimeout> | undefined;
  #heartbeat: ReturnType<typeof setTimeout> | undefined;
  #stopped = true;
  #attempt = 0;
  #keepaliveMs = 30_000;
  #generation = 0;
  constructor(
    private readonly options: {
      readonly config: ClientConfig;
      readonly credential: () => Promise<Result<Credential>>;
      readonly channel: string;
      readonly state: (state: ConnectionState) => void;
      readonly event: (value: unknown) => void;
      readonly resynchronise: () => void;
    },
  ) {}
  /** Start one subscription. */
  start() {
    this.#stopped = false;
    void this.connect();
  }
  private armHeartbeat(socket: WebSocket) {
    clearTimeout(this.#heartbeat);
    this.#heartbeat = setTimeout(() => socket.close(), this.#keepaliveMs);
  }
  private async connect() {
    if (this.#stopped) return;
    const generation = ++this.#generation;
    this.options.state(this.#attempt ? "reconnecting" : "connecting");
    const credential = await this.options.credential();
    if (this.#stopped || generation !== this.#generation) return;
    if (!credential.ok) {
      this.retry();
      return;
    }
    const authorization = {
      host: this.options.config.eventsHost,
      Authorization: `WS_${credential.value.reveal()}`,
    };
    const encoded = btoa(JSON.stringify(authorization))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
    let socket: WebSocket;
    try {
      socket = new WebSocket(this.options.config.realtime, [
        "aws-appsync-event-ws",
        `header-${encoded}`,
      ]);
    } catch {
      this.retry();
      return;
    }
    this.#socket = socket;
    const id = crypto.randomUUID();
    socket.onopen = () => {
      socket.send(JSON.stringify({ type: "connection_init" }));
      this.armHeartbeat(socket);
    };
    socket.onmessage = (event) => {
      if (this.#socket !== socket || this.#stopped) return;
      let raw: unknown;
      try {
        raw = JSON.parse(String(event.data));
      } catch {
        this.options.resynchronise();
        return;
      }
      const result = Schema.decodeUnknownEither(envelope)(raw);
      if (Either.isLeft(result)) {
        this.options.resynchronise();
        return;
      }
      const message = result.right;
      if (message.type === "connection_ack") {
        this.#keepaliveMs = message.connectionTimeoutMs ?? 30_000;
        this.armHeartbeat(socket);
        socket.send(
          JSON.stringify({
            type: "subscribe",
            id,
            channel: this.options.channel,
            authorization,
          }),
        );
      } else if (message.type === "ka") {
        this.armHeartbeat(socket);
      } else if (message.type === "subscribe_success" && message.id === id) {
        this.#attempt = 0;
        this.options.state("live");
        // Fetch after subscribing to close the gap during connection/reconnection.
        this.options.resynchronise();
      } else if (message.type === "data" && message.id === id) {
        const batch = Schema.decodeUnknownEither(eventBatch)(message.event);
        if (Either.isLeft(batch)) {
          this.options.resynchronise();
          return;
        }
        let missedEvent = false;
        const events: unknown[] = [];
        for (const serialized of batch.right) {
          if (typeof serialized !== "string") {
            missedEvent = true;
            continue;
          }
          try {
            events.push(JSON.parse(serialized));
          } catch {
            missedEvent = true;
          }
        }
        // A bad member must neither lose the rest of a batch nor leave stale
        // orders on screen. Start reconciliation before applying the valid peers
        // so an older snapshot cannot immediately remove their updates.
        if (missedEvent) this.options.resynchronise();
        for (const payload of events) this.options.event(payload);
      } else if (message.type === "broadcast_error" && message.id === id) {
        this.options.resynchronise();
      } else if (
        (message.type === "subscribe_error" && message.id === id) ||
        message.type === "connection_error"
      )
        socket.close();
    };
    socket.onerror = () => socket.close();
    socket.onclose = () => {
      if (this.#socket === socket) {
        clearTimeout(this.#heartbeat);
        this.#socket = undefined;
        this.retry();
      }
    };
  }
  private retry() {
    if (this.#stopped) return;
    this.options.state(navigator.onLine ? "reconnecting" : "offline");
    clearTimeout(this.#retry);
    this.#retry = setTimeout(
      () => void this.connect(),
      Math.min(30_000, 1_000 * 2 ** this.#attempt++),
    );
  }
  /** Suspend/dispose a subscription and all timers. */
  stop() {
    this.#stopped = true;
    this.#generation++;
    clearTimeout(this.#retry);
    clearTimeout(this.#heartbeat);
    const socket = this.#socket;
    this.#socket = undefined;
    socket?.close();
  }
}
